import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { captureEvent } from '@/lib/posthog';

type EventType = 'summary_generated' | 'audio_played' | 'full_episode_played' | 'site_visit' | 'podcast_searched' | 'youtube_searched';

/** Strip 'episode-' prefix so the value is a valid UUID for database storage */
function sanitizeBriefId(id: string | undefined): string | null {
  if (!id) return null;
  return id.replace(/^episode-/, '') || null;
}

interface LogEventParams {
  eventType: EventType;
  briefId?: string;
  language?: string;
  metadata?: Json;
}

export async function logAnalyticsEvent({
  eventType,
  briefId,
  language,
  metadata = {},
}: LogEventParams): Promise<void> {
  // Always forward to PostHog (works for both authenticated and anonymous users)
  try {
    captureEvent(eventType, {
      brief_id: briefId || undefined,
      language: language || undefined,
      ...(typeof metadata === 'object' && metadata !== null ? metadata as Record<string, unknown> : {}),
    });
  } catch {
    // Silently fail - analytics should not disrupt user experience
  }

  // Also log to Supabase for internal analytics (requires authentication)
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      // User not authenticated - skip Supabase analytics
      return;
    }

    const { error } = await supabase.from('analytics_events').insert([{
      user_id: user.id,
      brief_id: sanitizeBriefId(briefId),
      event_type: eventType,
      language: language || null,
      metadata,
      client_platform: 'web',
    }]);

    if (error) {
      // Silently fail - analytics should not disrupt user experience
    }
  } catch {
    // Silently fail - analytics should not disrupt user experience
  }
}

// ── Engagement tracking ─────────────────────────────────────────────

interface BaseEngagementParams {
  eventType: 'start';
  source: 'podcast' | 'youtube';
  durationSeconds: number;
  progressSeconds: number;
  progressPercentage: number;
  sessionId: string | null;
}

interface SummaryEngagementParams extends BaseEngagementParams {
  masterBriefId: string;
}

interface EpisodeEngagementParams extends BaseEngagementParams {
  contentId: string;
}

/** Log a summary (brief) listening engagement event */
export async function logSummaryEngagement(params: SummaryEngagementParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const cleanId = sanitizeBriefId(params.masterBriefId);
    if (!cleanId) return;

    // Dedup: one start per summary per user
    const { data: existing } = await supabase
      .from('summary_engagement_events')
      .select('id')
      .eq('user_id', user.id)
      .eq('master_brief_id', cleanId)
      .eq('event_type', 'start')
      .maybeSingle();
    if (existing) return;

    await supabase.from('summary_engagement_events').insert({
      user_id: user.id,
      master_brief_id: cleanId,
      source: params.source,
      event_type: 'start',
      duration_seconds: params.durationSeconds,
      progress_seconds: params.progressSeconds,
      progress_percentage: params.progressPercentage,
      session_id: params.sessionId,
      client_platform: 'web',
    });
  } catch {
    // Silently fail
  }
}

/** Log an episode (direct playback) listening engagement event */
export async function logEpisodeEngagement(params: EpisodeEngagementParams): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (!params.contentId) return;

    // Dedup: one start per episode per source per user
    const { data: existing } = await supabase
      .from('episode_engagement_events')
      .select('id')
      .eq('user_id', user.id)
      .eq('content_id', params.contentId)
      .eq('source', params.source)
      .eq('event_type', 'start')
      .maybeSingle();
    if (existing) return;

    await supabase.from('episode_engagement_events').insert({
      user_id: user.id,
      content_id: params.contentId,
      source: params.source,
      event_type: 'start',
      duration_seconds: params.durationSeconds,
      progress_seconds: params.progressSeconds,
      progress_percentage: params.progressPercentage,
      session_id: params.sessionId,
      client_platform: 'web',
    });
  } catch {
    // Silently fail
  }
}
